import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Flex, Segmented, Skeleton, Table, Tag, Typography } from 'antd'
import { FieldTimeOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, EmptyState, emptyStates, Well } from '@pushly/aqe/lib/components'
import type { EmptyStateProps, EmptyStatePresetConfig } from '@pushly/aqe/lib/components'
import { useService } from '@pushly/aqe/lib/hooks/use-service'
import { PageAnalysisRuleState, PageAnalysisScope, UrlRulePolicy } from '@pushly/models/lib/enums'
import {
    ageLimitSentence,
    domainHasFlag,
    GATES_DESCRIPTION,
    RULE_STATE_DESCRIPTIONS,
    RULE_STATE_SHORT_LABELS,
    ruleSentence,
    SCOPE_DESCRIPTIONS,
    SCOPE_LABELS,
    scopeTagLabel,
} from './constants'
import { FEAT_PAGE_ANALYSIS_IN_ETL } from '../../constants'
import { GateAgeLimitDrawer } from './gate-age-limit-drawer'
import { GateRuleDrawer } from './gate-rule-drawer'
import { PageAnalysisGateService } from '../../services/page-analysis-gate.service'
import { DomainDto } from '../../dtos/domain'
import { IPageAnalysisContentAgeLimit, IPageAnalysisUrlRule } from '../../interfaces/page-analysis-gates'
import { VERTICAL_WELL_GAP } from '../../theme/constants'
import { getClassNames } from '../../_utils/classnames'
import { simpleNotification } from '../../_utils/utils'

interface IPageAnalysisSettings {
    loading: boolean
    domain?: DomainDto
}

type LoadState = 'loading' | 'loaded' | 'failed'

const SCOPE_OPTIONS = [PageAnalysisScope.DIRECT, PageAnalysisScope.ETL].map((scope) => ({
    value: scope,
    label: SCOPE_LABELS[scope],
}))

const STATE_OPTIONS = [PageAnalysisRuleState.DISABLED, PageAnalysisRuleState.TRACE, PageAnalysisRuleState.ACTIVE].map(
    (state) => ({ value: state, label: RULE_STATE_SHORT_LABELS[state] }),
)

/**
 * The `emptyStates` presets are shaped for a DataView `noData` slot, which names
 * the same parts `text` and `action`. EmptyState names them `title` and `actions`.
 */
const asEmptyStateProps = (preset: EmptyStatePresetConfig): EmptyStateProps => ({
    icon: preset.icon,
    title: preset.text,
    description: preset.description,
    actions: preset.action,
    footer: preset.footer,
})

/**
 * The two Enrichment Gates for one traffic path at a time.
 *
 * The two gates load independently so one failed read leaves the other readable,
 * and a failed read is never drawn as an empty one. On a gate those are opposite
 * facts: "this domain declines nothing" invites a second set of rules on top of
 * the set that is already there.
 */
export const PageAnalysisSettings: FC<IPageAnalysisSettings> = ({ loading, domain }) => {
    const gateService = useService<PageAnalysisGateService>(PageAnalysisGateService)

    const [scope, setScope] = useState<PageAnalysisScope>(PageAnalysisScope.DIRECT)
    const [rulesState, setRulesState] = useState<LoadState>('loading')
    const [limitsState, setLimitsState] = useState<LoadState>('loading')
    const [rules, setRules] = useState<IPageAnalysisUrlRule[]>([])
    const [ageLimits, setAgeLimits] = useState<IPageAnalysisContentAgeLimit[]>([])
    const [ruleDrawerOpen, setRuleDrawerOpen] = useState(false)
    const [editingRule, setEditingRule] = useState<IPageAnalysisUrlRule | undefined>()
    const [ageDrawerOpen, setAgeDrawerOpen] = useState(false)
    const [savingLimitState, setSavingLimitState] = useState(false)
    const [savingRuleId, setSavingRuleId] = useState<number | undefined>()

    /** One drawer serves both, so the rule it is opened on is what picks the mode. */
    const openRuleDrawer = (rule?: IPageAnalysisUrlRule) => {
        setEditingRule(rule)
        setRuleDrawerOpen(true)
    }

    const domainId = domain?.id

    const loadRules = useCallback(async () => {
        if (!domainId) {
            return
        }

        setRulesState('loading')

        const res = await gateService.fetchUrlRules(domainId)

        // aqe signals failure with `ok`; it never assigns `error`, so testing
        // `error` here would let a failed read render as an empty one.
        if (!res.ok) {
            setRulesState('failed')
            return
        }

        setRules(res.data ?? [])
        setRulesState('loaded')
    }, [domainId, gateService])

    const loadAgeLimits = useCallback(async () => {
        if (!domainId) {
            return
        }

        setLimitsState('loading')

        const res = await gateService.fetchContentAgeLimits(domainId)

        if (!res.ok) {
            setLimitsState('failed')
            return
        }

        setAgeLimits(res.data ?? [])
        setLimitsState('loaded')
    }, [domainId, gateService])

    useEffect(() => {
        loadRules()
    }, [loadRules])

    useEffect(() => {
        loadAgeLimits()
    }, [loadAgeLimits])

    const scopedRules = useMemo(() => rules.filter((rule) => rule.scopes.includes(scope)), [rules, scope])
    const ageLimit = useMemo(() => ageLimits.find((limit) => limit.scope === scope), [ageLimits, scope])

    /**
     * Same bargain as the limit's state: written from the row, moved first and
     * moved back if the write is refused.
     */
    const changeRuleState = async (rule: IPageAnalysisUrlRule, next: PageAnalysisRuleState) => {
        if (!domainId || next === rule.state) {
            return
        }

        const applyState = (state: PageAnalysisRuleState) =>
            setRules((current) => current.map((r) => (r.id === rule.id ? { ...r, state } : r)))

        applyState(next)
        setSavingRuleId(rule.id)

        // The route validates the body as a whole rule, so the fields the row
        // does not edit go back with it.
        const res = await gateService.updateUrlRule(domainId, rule.id, {
            policy: rule.policy,
            matchType: rule.matchType,
            value: rule.value,
            scopes: rule.scopes,
            state: next,
            description: rule.description,
        })

        setSavingRuleId(undefined)

        if (!res.ok) {
            applyState(rule.state)
            simpleNotification('error', 'The state could not be saved.')
        }
    }

    /**
     * The state is the one part of a limit worth changing without opening the
     * drawer, so it is written from the row. There is no draft layer here yet,
     * so this click is the commit: the row moves first and goes back if the
     * write is refused, rather than reading as saved when it is not.
     */
    const changeLimitState = async (next: PageAnalysisRuleState) => {
        if (!domainId || !ageLimit || next === ageLimit.state) {
            return
        }

        const previous = ageLimit.state
        const applyState = (state: PageAnalysisRuleState) =>
            setAgeLimits((current) => current.map((l) => (l.scope === scope ? { ...l, state } : l)))

        applyState(next)
        setSavingLimitState(true)

        // PUT replaces the limit, so the fields the row does not edit have to
        // be sent back as they are.
        const res = await gateService.putContentAgeLimit(domainId, scope, {
            maxAgeSeconds: ageLimit.maxAgeSeconds,
            maxAgeDisplayMetric: ageLimit.maxAgeDisplayMetric,
            unknownPolicy: ageLimit.unknownPolicy,
            state: next,
        })

        setSavingLimitState(false)

        if (!res.ok) {
            applyState(previous)
            simpleNotification('error', 'The state could not be saved.')
        }
    }

    /**
     * The one thing people get wrong: these are deny-by-default with an
     * allow-list escape hatch, so a single "only analyze" rule silently closes
     * everything else in the scope. Say so at the moment it becomes true.
     */
    const allowRule = scopedRules.find(
        (rule) => rule.policy === UrlRulePolicy.ALLOW && rule.state !== PageAnalysisRuleState.DISABLED,
    )
    const allowRuleEnforced = allowRule?.state === PageAnalysisRuleState.ACTIVE

    /**
     * Background analysis is itself flagged. With it off nothing reaches these
     * gates on that path, so the rules would be describing traffic that never
     * arrives. On-demand analysis is unaffected.
     */
    const backgroundOff = scope === PageAnalysisScope.ETL && !domainHasFlag(domain, FEAT_PAGE_ANALYSIS_IN_ETL)

    const columns = [
        {
            title: 'Rule',
            key: 'rule',
            render: (_: unknown, rule: IPageAnalysisUrlRule) => (
                <Flex vertical gap={2}>
                    <Typography.Text>{ruleSentence(rule)}</Typography.Text>
                    {/* What is stored, under what it means: the sentence is a reading of these two. */}
                    <Typography.Text className="ai-muted-text ai-rule-stored">
                        {`${rule.policy} · ${rule.matchType}`}
                    </Typography.Text>
                </Flex>
            ),
        },
        {
            title: 'Applies to',
            key: 'scopes',
            width: 130,
            render: (_: unknown, rule: IPageAnalysisUrlRule) => <Tag>{scopeTagLabel(rule.scopes)}</Tag>,
        },
        {
            title: 'State',
            key: 'state',
            width: 210,
            render: (_: unknown, rule: IPageAnalysisUrlRule) => (
                <Segmented
                    size="small"
                    value={rule.state}
                    disabled={savingRuleId === rule.id}
                    onChange={(next) => changeRuleState(rule, next as PageAnalysisRuleState)}
                    options={STATE_OPTIONS}
                />
            ),
        },
        {
            key: 'actions',
            width: 80,
            align: 'right' as const,
            render: (_: unknown, rule: IPageAnalysisUrlRule) => (
                <Button size="small" onClick={() => openRuleDrawer(rule)}>
                    Edit
                </Button>
            ),
        },
    ]

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP} className={getClassNames('page-analysis-settings')}>
            <Well
                title={
                    <Flex align="center" gap={8}>
                        Page Analysis
                        <Tag className="ai-title-tag">Internal only</Tag>
                    </Flex>
                }
                footerMode="hidden"
            >
                <Skeleton loading={loading} active={true} title={false}>
                    <Typography.Paragraph className="ai-muted-text">
                        Reads the pages your audience opens and turns them into something the rest of the suite can use:
                        clean article text, a classification, keywords and imagery. It is what Brand Voice writes from
                        and what the Curator ranks.
                    </Typography.Paragraph>
                </Skeleton>
            </Well>

            <Well
                title="Enrichment Gates"
                actions={
                    <Segmented
                        value={scope}
                        onChange={(next) => setScope(next as PageAnalysisScope)}
                        options={SCOPE_OPTIONS}
                    />
                }
                footerMode="hidden"
            >
                <Flex vertical gap={12}>
                    <Typography.Text className="ai-muted-text">{GATES_DESCRIPTION}</Typography.Text>

                    <Typography.Text className="ai-muted-text">{SCOPE_DESCRIPTIONS[scope]}</Typography.Text>

                    {rulesState === 'loaded' && !!allowRule && (
                        <Alert
                            type="warning"
                            showIcon={true}
                            message={
                                allowRuleEnforced
                                    ? 'One "only analyze" rule is enforced, so a page outside it is declined even though no rule names it.'
                                    : 'This scope has an "only analyze" rule in reporting only. The moment it is enforced, everything it does not name is declined.'
                            }
                        />
                    )}

                    {backgroundOff && (
                        <Alert
                            type="info"
                            showIcon={true}
                            message="Background analysis is off for this domain"
                            description="Pages opened by subscribers are not analyzed, so nothing below is acting on anything today. These rules take effect again if background analysis is turned back on."
                        />
                    )}

                    <Well.Section title="URL rules">
                        {rulesState === 'loading' && <Skeleton active={true} title={false} />}

                        {rulesState === 'failed' && (
                            <EmptyState
                                size="small"
                                {...asEmptyStateProps(
                                    emptyStates.error({
                                        text: 'URL rules could not be read',
                                        description:
                                            'This domain may already have rules. Do not add one until this loads, or you will author a second set.',
                                        onRetry: loadRules,
                                        retryLabel: 'Try again',
                                    }),
                                )}
                            />
                        )}

                        {rulesState === 'loaded' && scopedRules.length === 0 && (
                            <EmptyState
                                size="small"
                                icon={<FilterOutlined />}
                                iconVariant="chip"
                                title={`No URL rules for ${SCOPE_LABELS[scope].toLowerCase()}`}
                                description="Every page on this path gets the full analysis. A rule narrows that to the paths that carry articles."
                                actions={
                                    <Button type="primary" onClick={() => openRuleDrawer()}>
                                        Add the first rule
                                    </Button>
                                }
                            />
                        )}

                        {rulesState === 'loaded' && scopedRules.length > 0 && (
                            <Flex vertical gap={8}>
                                <Table
                                    size="small"
                                    rowKey="id"
                                    pagination={false}
                                    dataSource={scopedRules}
                                    columns={columns}
                                />

                                {/* The empty state's CTA is the only way in until the table replaces it. */}
                                <div>
                                    <Button
                                        type="dashed"
                                        size="small"
                                        icon={<PlusOutlined />}
                                        onClick={() => openRuleDrawer()}
                                    >
                                        Add a URL rule
                                    </Button>
                                </div>
                            </Flex>
                        )}
                    </Well.Section>

                    <Well.Section
                        title={
                            <Flex align="center" justify="space-between" gap={12}>
                                Content age limit
                                {limitsState === 'loaded' && !!ageLimit && (
                                    <Button
                                        size="small"
                                        className="ai-section-edit"
                                        onClick={() => setAgeDrawerOpen(true)}
                                    >
                                        Edit
                                    </Button>
                                )}
                            </Flex>
                        }
                    >
                        {limitsState === 'loading' && <Skeleton active={true} title={false} />}

                        {limitsState === 'failed' && (
                            <EmptyState
                                size="small"
                                {...asEmptyStateProps(
                                    emptyStates.error({
                                        text: 'The age limit could not be read',
                                        description:
                                            'This path may already have a limit. Setting one from here would overwrite whatever is there.',
                                        onRetry: loadAgeLimits,
                                        retryLabel: 'Try again',
                                    }),
                                )}
                            />
                        )}

                        {limitsState === 'loaded' && !ageLimit && (
                            <EmptyState
                                size="small"
                                icon={<FieldTimeOutlined />}
                                iconVariant="chip"
                                title="No age limit"
                                description="Articles are analyzed however old they are. A limit trades older content out of the recommendation pool, so check what this domain publishes before adding one."
                                actions={
                                    <Button type="primary" onClick={() => setAgeDrawerOpen(true)}>
                                        Set an age limit
                                    </Button>
                                }
                            />
                        )}

                        {limitsState === 'loaded' && !!ageLimit && (
                            <Flex align="center" gap={16} wrap="wrap">
                                <Typography.Text>{ageLimitSentence(ageLimit)}</Typography.Text>

                                <Segmented
                                    value={ageLimit.state}
                                    disabled={savingLimitState}
                                    onChange={(next) => changeLimitState(next as PageAnalysisRuleState)}
                                    options={STATE_OPTIONS}
                                />

                                <Typography.Text className="ai-muted-text ai-section-note">
                                    {RULE_STATE_DESCRIPTIONS[ageLimit.state]}
                                </Typography.Text>
                            </Flex>
                        )}
                    </Well.Section>
                </Flex>
            </Well>

            <GateRuleDrawer
                open={ruleDrawerOpen}
                domainId={domainId}
                scope={scope}
                rule={editingRule}
                onClose={() => setRuleDrawerOpen(false)}
                onSaved={loadRules}
            />

            <GateAgeLimitDrawer
                open={ageDrawerOpen}
                domainId={domainId}
                scope={scope}
                limit={ageLimit}
                onClose={() => setAgeDrawerOpen(false)}
                onSaved={loadAgeLimits}
            />
        </Flex>
    )
}
